/**
 * Forgot-password page. Collects an email, POSTs to /api/auth/forgot-password,
 * and always shows the same neutral confirmation — the API never reveals whether
 * an account exists, and neither does this screen.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Loader2, MailCheck } from 'lucide-react';
import { api } from '@/lib/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-2 p-4">
      <div className="card w-full max-w-sm p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <BookOpen size={22} className="text-accent" />
          <span className="text-lg font-semibold">STR Wiki</span>
        </div>

        {sent ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded bg-surface-3 p-3 text-sm text-ink-muted">
              <MailCheck size={16} className="mt-0.5 flex-shrink-0 text-accent" />
              <p>
                If that address has an account, we&apos;ve sent a reset link. Check your
                inbox — the link expires in 30 minutes.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="btn btn-primary w-full"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            <p className="mb-4 text-sm text-ink-muted">
              Enter your email and we&apos;ll send you a link to reset your password.
            </p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1 block text-xs font-medium text-ink-muted">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="you@settheroom.com"
                />
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <button type="submit" disabled={busy} className="btn btn-primary w-full">
                {busy && <Loader2 size={15} className="animate-spin" />}
                Send reset link
              </button>
            </form>
            <p className="mt-4 text-center text-xs text-ink-muted">
              <Link to="/login" className="text-accent hover:underline">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
